import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { addMaterial, clearRefresh } from "../utils/materialsSlice";
import { BASE_URL } from "../utils/constants";
import TableWithPaginationComponent from "./TableWithPaginationComponent";
import Loader from "./Loader";
import MaterialDetail from "./MaterialDetail";

const MaterialList = () => {
  const materialListData = useSelector((state) => state.material.list);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreateNewMaterial, setShowCreateNewMaterial] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const refreshNeeded = useSelector((state) => state.material.refreshNeeded);

  useEffect(() => {
    if (refreshNeeded) {
      fetchMaterialList(page, limit, debouncedSearch);
      dispatch(clearRefresh());
    }
  }, [refreshNeeded]);

  useEffect(() => {
    if (!selectedId) {
      setSearchTerm("");
      setDebouncedSearch("");
      setPage(1);
    }
  }, [selectedId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchMaterialList(page, limit, debouncedSearch);
  }, [page, debouncedSearch]);

  const fetchMaterialList = async (page = 1, limit = 10, search = "") => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/materialMaster`, {
        params: {
          page,
          limit,
          search,
        },
        withCredentials: true,
      });

      dispatch(addMaterial(res.data.data));
      setPaginationInfo(res.data.meta);
    } catch (err) {
      console.error("Error fetching material list:", err);
      dispatch(addMaterial([]));
      setPaginationInfo({ totalCount: 0, page: 1, limit, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
  };

  const goToDetailPage = () => {
    setShowCreateNewMaterial(true);
    setSelectedId(null);
  };

  const listClickHandler = (id) => {
    setSelectedId(id);
    setShowCreateNewMaterial(false);
  };

  const goBackToList = () => {
    setSelectedId(null);
    setShowCreateNewMaterial(false);
  };

  const onDeleteRawMaterial = async (id) => {
    try {
      const confirmed = window.confirm("Are you sure you want to delete this material?");
      if (!confirmed) return;

      const res = await axios.delete(`${BASE_URL}/materialMaster/${id}`, {
        withCredentials: true,
      });

      console.log(res.data.message);
      alert("Material deleted successfully!");

      await fetchMaterialList();

    } catch (error) {
      console.error(error);
      alert(
        error?.response?.data?.message ||
        "Failed to delete material. Please try again."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size={12} text="Loading Application..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {selectedId === null && !showCreateNewMaterial && (
        <div className="px-6 pt-2 py-1">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-4xl font-bold text-gray-900">Material List</h1>
            <button
              onClick={goToDetailPage}
              className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 transition"
            >
              + Create New
            </button>
          </div>

          {/* Search input */}
          <div className="relative mb-3 w-full md:w-1/2">
            <input
              type="text"
              placeholder="Search by Material Name or Code"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-600"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xl"
              >
                &times;
              </button>
            )}
          </div>

          {/* Table or message */}
          {materialListData.length === 0 ? (
            <div className="text-gray-500 text-center mt-10">
              No materials found.
            </div>
          ) : (
            <TableWithPaginationComponent
              materials={materialListData}
              pagination={paginationInfo}
              onPageChange={(p) => setPage(p)}
              listClickHandler={listClickHandler}
              onDelete={onDeleteRawMaterial}
            />
          )}
        </div>
      )}

      {selectedId && (
        <MaterialDetail id={selectedId} goBackToList={goBackToList} />
      )}

      {showCreateNewMaterial && <MaterialDetail goBackToList={goBackToList} />}
    </div>
  );
};

export default MaterialList;
